// client/src/pages/sections/groups/BulkConfirmBar.js
// "Confirm all N" at the top of a one-click review group (relationship-group confirmations, standalone individuals).
// Posts to /api/diBulkResolve, which runs the normal verb for each of the caller's OWN open items in chunks and says how many
// remain; this asks again until none do, so a long list never hits the ~45 s web-request limit. Every confirmation it makes
// is the same one a single click makes (same audit rows, same test-mode undo). Asks once before it does anything.

import { useState } from 'react';
import { emitToast } from './toast';

const COPY = {
  confirm_rg: {
    noun: 'relationship groups',
    ask: (n) => `Confirm all ${n} relationship groups? Each one is recorded as your confirmation. You can still open any of them, change it, or take a confirmation back afterwards.`,
    done: (n) => `Confirmed ${n} relationship group${n === 1 ? '' : 's'}.`,
  },
  confirm_standalone: {
    noun: 'standalone individuals',
    ask: (n) => `Confirm all ${n} as standalone individuals? Each one is recorded as your review. You can still move any client into a relationship group afterwards.`,
    done: (n) => `Confirmed ${n} standalone individual${n === 1 ? '' : 's'}.`,
  },
};

async function postBulk(verb, period, testMode, testSessionId) {
  const res = await fetch('/api/diBulkResolve', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verb, period, testMode: Boolean(testMode), sessionId: testMode ? testSessionId : null }),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* plain text */ }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return JSON.parse(text);
}

export default function BulkConfirmBar({ verb, count, period, testMode, testSessionId, disabledReason, onDone }) {
  const [stage, setStage] = useState('idle');          // idle | ask | running
  const [progress, setProgress] = useState(null);
  const copy = COPY[verb];
  if (!copy || !(count > 0)) return null;

  async function run() {
    setStage('running');
    let done = 0;
    let failed = [];
    try {
      for (let round = 0; round < 100; round++) {
        const r = await postBulk(verb, period, testMode, testSessionId);
        done += r.done;
        failed = failed.concat(r.failed || []);
        setProgress({ done, failed: failed.length });
        if (r.remaining === 0 || (r.done === 0 && (r.failed || []).length === 0)) break;   // finished, or nothing moved: never loop
      }
      if (done > 0) emitToast({ kind: 'success', message: copy.done(done) });
      if (failed.length) {
        emitToast({ kind: 'error', message: `${failed.length} could not be confirmed (${failed[0].clientCode}: ${failed[0].error}). The rest were.` });
      }
      if (done === 0 && failed.length === 0) emitToast({ kind: 'info', message: 'Nothing left to confirm.' });
    } catch (err) {
      emitToast({ kind: 'error', message: `Couldn't confirm: ${err.message}${done ? ` (${done} were confirmed before it stopped)` : ''}.` });
    } finally {
      setStage('idle');
      setProgress(null);
      if (onDone) onDone();
    }
  }

  if (stage === 'running') {
    return (
      <div className="cg-bulk-bar" role="status">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
        <span>Confirming{progress ? ` · ${progress.done.toLocaleString()} done` : ''}…</span>
      </div>
    );
  }

  if (stage === 'ask') {
    return (
      <div className="cg-bulk-bar cg-bulk-bar--ask">
        <span className="cg-bulk-text">{copy.ask(count.toLocaleString())}</span>
        <button type="button" className="cg-btn cg-btn--primary" onClick={run}>Confirm all {count.toLocaleString()}</button>
        <button type="button" className="cg-btn cg-btn--secondary" onClick={() => setStage('idle')}>Cancel</button>
      </div>
    );
  }

  return (
    <div className="cg-bulk-bar">
      <button
        type="button"
        className="cg-btn cg-btn--primary"
        disabled={Boolean(disabledReason)}
        title={disabledReason || `Confirm every open one of your ${copy.noun} at once`}
        onClick={() => setStage('ask')}
      >
        Confirm all {count.toLocaleString()} {copy.noun}
      </button>
      {disabledReason && <span className="cg-cell-hint">{disabledReason}</span>}
    </div>
  );
}
